import React, { useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import dayjs from 'dayjs';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isBetween from 'dayjs/plugin/isBetween';

import { TbBellExclamation } from 'react-icons/tb';

import type { IconProps } from 'components-new/icon/icon';
import Icon from 'components-new/icon/icon';
import Typography from 'components-new/typography/typography';
import Banner from 'components-new/banner/banner';
import Tooltip from 'components-new/tooltip/tooltip';

import getIconForJobType from 'pages/organization/OrganizationWeekPlannerPage/utils/get-icon-for-job-type';

import type { OrganizationJob } from 'state/remote/types';

import { createLocale } from 'locales';

import JobPopover from '../job-popover/job-popover';

dayjs.extend(isTomorrow);
dayjs.extend(isBetween);

const globalLocalize = createLocale('global');

interface CardKanbanProps {
  job: OrganizationJob;
  iconProps?: IconProps;
  className?: string;
}

const localize = createLocale('organizationWeekPlannerLocales');

const KanbanCard: React.FC<CardKanbanProps> = (props) => {
  const { job } = props;

  const alertInterestJobNoLongerAssigned48Hrs =
    job.is_interest &&
    !job.assigned_to &&
    dayjs(job.date).isBetween(dayjs(), dayjs().add(48, 'hour')) && // between now and 48 hrs from now
    localize('alertInterestJobNoLongerAssigned48HrsMessage');

  const alertInterestShownWithoutAssignedFreelancer =
    job.is_interest_shown &&
    !job.assigned_to &&
    !alertInterestJobNoLongerAssigned48Hrs &&
    localize('alertInterestShownWithoutAssignedFreelancerMessage');

  const alertPersonalJobStartsTomorrowNotAccepted = useMemo(
    () =>
      job.is_personal &&
      !job.assigned_to &&
      dayjs(job.date).isTomorrow() &&
      localize('alertPersonalJobStartsTomorrowNotAcceptedMessage'),

    [job],
  );

  const hasAlerts =
    alertPersonalJobStartsTomorrowNotAccepted ||
    alertInterestShownWithoutAssignedFreelancer ||
    alertInterestJobNoLongerAssigned48Hrs;

  const cardClassNames = twMerge(
    'flex flex-col justify-between gap-y-4 hover:shadow-10',
    'py-2 px-3 lg:mx-3',
    'rounded-lg shadow-sm',
    'outline outline-[2px] outline-gray100',
    alertInterestShownWithoutAssignedFreelancer && 'outline-warning',
    (alertPersonalJobStartsTomorrowNotAccepted ||
      alertInterestJobNoLongerAssigned48Hrs) &&
      'outline-destructive',
    !hasAlerts && 'hover:outline-gray200',
    props.className,
  );

  let jobIcon = null;
  if (job.is_personal) {
    jobIcon = getIconForJobType('personal');
  } else if (job.is_urgent) {
    jobIcon = getIconForJobType('urgent');
  } else if (job.is_interest) {
    jobIcon = getIconForJobType('interest');
  }

  const mainIconTooltip = twMerge(
    job.is_personal && localize('thisIsPersonalAssignment'),
    job.is_urgent && !job.is_personal && localize('thisIsUrgentAssignment'),
    job.is_interest && localize('thisIsIntrestAssignment'),
  );

  return (
    <div className={cardClassNames}>
      <div className="flex justify-between items-center w-full gap-x-2">
        <div className="flex flex-row gap-1 items-center">
          <Tooltip
            text={mainIconTooltip}
            tooltipTriggerProps={{
              asChild: false,
              className: 'cursor-default',
            }}
          >
            {jobIcon && (
              <Icon
                className="text-gray700"
                size={20}
                icon={jobIcon}
                {...props.iconProps}
              />
            )}
          </Tooltip>
          {job.assigned_to && (
            <Typography
              className="text-gray600"
              variant="body-medium-bold"
              as="p"
            >
              {job.assigned_to.name}
            </Typography>
          )}
          {!job.assigned_to && (
            <Banner title={globalLocalize('open')} variant="destructive" />
          )}

          {(alertInterestJobNoLongerAssigned48Hrs ||
            alertPersonalJobStartsTomorrowNotAccepted ||
            alertInterestShownWithoutAssignedFreelancer) && (
            <Tooltip
              tooltipTriggerProps={{
                asChild: false,
                className: 'cursor-default',
              }}
              text={
                alertInterestJobNoLongerAssigned48Hrs ||
                alertInterestShownWithoutAssignedFreelancer ||
                alertPersonalJobStartsTomorrowNotAccepted ||
                ''
              }
            >
              <Icon
                size={20}
                icon={TbBellExclamation}
                className={twMerge(
                  'ml-auto',
                  alertInterestShownWithoutAssignedFreelancer &&
                    'text-warning-400',
                  (alertPersonalJobStartsTomorrowNotAccepted ||
                    alertInterestJobNoLongerAssigned48Hrs) &&
                    'text-destructive-400',
                )}
              />
            </Tooltip>
          )}
        </div>
        <JobPopover job={job} />
      </div>
      <div className="flex flex-col gap-y-2 text-gray500">
        <Typography variant="body-medium" as="p">
          {job.location.name}
        </Typography>
        <Typography variant="body-medium" as="p">
          {`${dayjs(job.start, 'HH:mm:ss').format('HH:mm')} - ${dayjs(
            job.end,
            'HH:mm:ss',
          ).format('HH:mm')}`}
        </Typography>
      </div>
    </div>
  );
};

export default KanbanCard;
